import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BillingService } from '../services/billingService';
import { Card, Button } from '../components/ui';
import { motion, useReducedMotion, type Variants } from 'motion/react';

export default function CheckoutSuccessPage() {
    const { t } = useTranslation('billing');
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isVerifying, setIsVerifying] = useState(true);
    const [verified, setVerified] = useState(false);
    const prefersReducedMotion = useReducedMotion();

    // Minimal entrance for short checkout result page.
    const panelVariants: Variants = {
        hidden: prefersReducedMotion ? { opacity: 1 } : { opacity: 0, y: 12 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] as const } },
    };

    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        if (sessionId) {
            BillingService.verifyPayment(sessionId)
                .then(() => {
                    setVerified(true);
                })
                .catch(() => {
                    setVerified(true);
                })
                .finally(() => {
                    setIsVerifying(false);
                });
        } else {
            setIsVerifying(false);
            setVerified(true);
        }
    }, [searchParams]);

    const handleGoToProfile = () => {
        navigate('/profile');
    };

    return (
        <div className="min-h-screen bg-snapfy-bg flex items-center justify-center p-4">
            <motion.div
                className="max-w-md w-full text-center"
                variants={panelVariants}
                initial="hidden"
                animate="visible"
            >
                <Card className="p-8 text-center">
                    {isVerifying ? (
                        <>
                            <Loader2 className="w-16 h-16 text-snapfy-primary animate-spin mx-auto mb-4" />
                            <h1 className="text-2xl font-bold text-snapfy-text-primary mb-2">
                                {t('checkout.success.verifying', 'Verificando pagamento...')}
                            </h1>
                        </>
                    ) : (
                        <>
                            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-10 h-10 text-green-500" />
                            </div>
                            <h1 className="text-2xl font-bold text-snapfy-text-primary mb-2">
                                {t('checkout.success.title', 'Pagamento Confirmado!')}
                            </h1>
                            <p className="text-snapfy-text-secondary mb-6">
                                {t('checkout.success.message', 'Seus créditos foram adicionados à sua conta.')}
                            </p>
                            <Button
                                variant="solid"
                                className="w-full"
                                onClick={handleGoToProfile}
                            >
                                {t('checkout.success.button', 'Ver meus créditos')}
                            </Button>
                        </>
                    )}
                </Card>
            </motion.div>
        </div>
    );
}
